import type { FileInfo } from 'api/inventionDisclosure/types';
import { PATHNAMES } from 'constants/constants';

export interface IPatentDraftFile extends FileInfo {
  patentDraftFileType: PatentDraftFileType;
  patentDraftFileStatus: PatentDraftFileStatus;
}

export interface IPatentDraftSection {
  id: string;
  sectionRef: string;
  heading: string | null;
  content: string | null;
}

export interface IPatentDraftDocument {
  id: string;
  created: string;
  isMigrated: boolean;
  content: string | null;
}

export enum ReferencePatentEntryType {
  PATENT_ID = 'PATENT_ID',
  FILE = 'FILE',
}

/**
 * A single reference patent attached to a draft — either a published patent id
 * or an uploaded reference file. Mirrors the backend `ReferencePatentEntry` sealed type.
 */
type IReferencePatentData =
  | { type: ReferencePatentEntryType.PATENT_ID; patentId: string }
  | { type: ReferencePatentEntryType.FILE; fileId: string };

export interface IPatentDraftDetails {
  patentDraftId: string;
  name: string;
  contentGenerated: boolean;
  inventionDescription: string;
  content: string | null;
  initialContent: string | null;
  changes: string; // meta data
  draftingMode: PatentDraftMode;
  referencePatents: IReferencePatentData[];
  createdByUser: {
    email: string;
    userId: string;
    username: string;
  };
  createdAt: string;
  jurisdiction: TSupportedJurisdiction;
  language: TSupportedLanguage;
  document: IPatentDraftDocument | null;
  trackedChangesBase: IPatentDraftDocument | null;
  templateId?: string | null;

  customInstructionId?: string | null;
  sectionless: boolean;
}

export type TProofreaderCheckType = 'ANTECEDENCE' | 'DEPENDENCE' | 'REFERENCE_SIGNS';

export interface IProofreaderCheck {
  checkResultId: string;
  patentDraftId: string;
  type: TProofreaderCheckType;
  tagId?: string;
  tagIds?: string[];
  reasoning: string;
}

export enum TSupportedLanguage {
  ENGLISH = 'ENGLISH',
  FRENCH = 'FRENCH',
  GERMAN = 'GERMAN',
  CHINESE = 'CHINESE',
  KOREAN = 'KOREAN',
  JAPANESE = 'JAPANESE',
  ITALIAN = 'ITALIAN',
  SPANISH = 'SPANISH',
  HUNGARIAN = 'HUNGARIAN',
  BULGARIAN = 'BULGARIAN',
  DUTCH = 'DUTCH',
  PORTUGUESE = 'PORTUGUESE',
}

export enum TSupportedJurisdiction {
  EPO = 'EPO',
  USPTO = 'USPTO',
  PCT = 'PCT',
  KIPO = 'KIPO',
  JPO = 'JPO',
  CNIPA = 'CNIPA',
  HIPO = 'HIPO',
  CIPO = 'CIPO',
  AU = 'AU',
  FR = 'FR',
}

interface IGlossarySearchTermDefinitionEffect {
  quote: string;
  tagId: string;
}

export interface IGlossarySearchTermDefinitions {
  definitions: IGlossarySearchTermDefinitionEffect[];
  effects: IGlossarySearchTermDefinitionEffect[];
}

export interface IGlossaryRelatedTerm {
  term: string;
  location: IGlossarySearchTermDefinitionEffect;
}

export enum PatentDraftFileType {
  INVENTION_DESCRIPTION = 'INVENTION_DESCRIPTION',
  FIGURE = 'FIGURE',
  STYLE_REFERENCE = 'STYLE_REFERENCE',
  PRIOR_ART = 'PRIOR_ART',
  MARKUSH_IMAGE = 'MARKUSH_IMAGE',
  TABLE_IMAGE = 'TABLE_IMAGE',
}

export enum PatentDraftFileStatus {
  UPLOADED = 'UPLOADED',
  QUEUED = 'QUEUED',
  PROCESSING = 'PROCESSING',
  DONE = 'DONE',
  ERROR = 'ERROR',
}

export enum PatentDraftMode {
  MECHANICAL_PATENT = 'MECHANICAL_PATENT',
  CHEMICAL_PATENT = 'CHEMICAL_PATENT',
  DISCLOSURE = 'DISCLOSURE',
}

/**
 * Hub-level filter modes — two values covering the two hub routes (disclosure and patent).
 * Deliberately separate from {@link PatentDraftMode}: drafts themselves are never tagged
 * with these values; the patent hub uses PATENT as an umbrella to fetch both mechanical
 * and chemical drafts in one query.
 */
export enum DraftHubMode {
  DISCLOSURE = 'DISCLOSURE',
  PATENT = 'PATENT',
}

export const draftNavigationPaths: Record<PatentDraftMode, string> = {
  [PatentDraftMode.DISCLOSURE]: `${PATHNAMES.patentDrafting}/disclosure`,
  [PatentDraftMode.MECHANICAL_PATENT]: `${PATHNAMES.patentDrafting}/patent`,
  [PatentDraftMode.CHEMICAL_PATENT]: `${PATHNAMES.patentDrafting}/patent`,
};

export interface ExportTemplateSettings {
  font_family: string;
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  font_size: {
    title: number;
    headings: number;
  };
  page_size: {
    height: number;
    width: number;
  };
  new_page_sections: {
    [sectionId: string]: boolean;
  };
  claims_formatting: {
    format: string;
  } | null;
  paragraph_numbering: boolean;
  line_numbering: { count_by: number; restart: 'restart' | 'continuous' } | null;
}
